import type {
  ContactBookDTO,
  ContactDTO,
  ContactImportAction,
  ContactImportDTO,
  ContactImportRowDTO,
  ContactImportRowStatus,
  ContactIssueDTO,
  ContactIssueKind,
  Party,
} from "../data/contactsApi";

/**
 * The contact book, shaped for the screen.
 *
 * Grouping, counting and labelling only. Every judgement about whether
 * something is wrong is made on the server, where the same rules also guard the
 * spreadsheet path — a GSTIN checked in the browser and a GSTIN checked in a
 * sheet must never disagree, and the way to guarantee that is to check it in
 * one place.
 */

export const PARTY_LABEL: Record<Party, string> = {
  customer: "Customers",
  vendor: "Vendors",
};

/** Which side of the book a contact belongs to. */
export function inParty(contact: ContactDTO, party: Party): boolean {
  return contact.contact_type === party;
}

/**
 * Exceptions still asking for something.
 *
 * An ignored one is not counted and not gone: it stays in its group, greyed,
 * with the reason beside it. Removing it would make "we looked at this and
 * decided" indistinguishable from "nobody ever saw it".
 */
export function openIssues(contact: ContactDTO): ContactIssueDTO[] {
  return contact.issues.filter((issue) => !issue.ignored);
}

export function openIssueCount(contacts: ContactDTO[], party: Party): number {
  return contacts
    .filter((contact) => inParty(contact, party))
    .reduce((total, contact) => total + openIssues(contact).length, 0);
}

export interface IssueRow {
  contact: ContactDTO;
  issue: ContactIssueDTO;
}

export interface IssueGroup {
  kind: ContactIssueKind;
  label: string;
  rows: IssueRow[];
  /** Rows still asking for something, which is what the header counts. */
  open: number;
}

/**
 * One side of the book's exceptions, grouped by kind.
 *
 * Ordered by `issue_kinds` from the server rather than by size, so the groups
 * stay in the same place as a CA works through them. A list that reorders itself
 * as its counts fall is one where the thing you were about to click moves.
 */
export function groupIssues(
  book: ContactBookDTO,
  party: Party,
  search: string
): IssueGroup[] {
  const query = search.trim().toLowerCase();
  const matches = (contact: ContactDTO) =>
    query === "" ||
    (contact.contact_name ?? "").toLowerCase().includes(query) ||
    (contact.gst_no ?? "").toLowerCase().includes(query);

  const rows: IssueRow[] = [];
  for (const contact of book.contacts) {
    if (!inParty(contact, party) || !matches(contact)) continue;
    for (const issue of contact.issues) rows.push({ contact, issue });
  }

  return book.issue_kinds
    .map((kind) => {
      const forKind = rows.filter((row) => row.issue.kind === kind);

      return {
        kind,
        label: book.labels[kind] ?? kind,
        // Unignored first, so the work is at the top of its own group.
        rows: forKind.slice().sort((a, b) => Number(a.issue.ignored) - Number(b.issue.ignored)),
        open: forKind.filter((row) => !row.issue.ignored).length,
      };
    })
    .filter((group) => group.rows.length > 0);
}

/** Everyone on one side, filtered by the same search the exceptions use. */
export function listContacts(
  book: ContactBookDTO,
  party: Party,
  search: string
): ContactDTO[] {
  const query = search.trim().toLowerCase();

  return book.contacts.filter((contact) => {
    if (!inParty(contact, party)) return false;
    if (query === "") return true;

    return (
      (contact.contact_name ?? "").toLowerCase().includes(query) ||
      (contact.gst_no ?? "").toLowerCase().includes(query) ||
      (contact.place_of_contact_label ?? "").toLowerCase().includes(query)
    );
  });
}

/**
 * Which control an exception offers.
 *
 * Driven off the field the server names rather than off the kind, because
 * several kinds are fixed the same way — a missing GSTIN and an invalid one are
 * both a GSTIN box — and two kinds are not fixable from here at all.
 */
export type FixControl = "gstin" | "pan" | "state" | "treatment" | "tds" | "udyam" | "name" | "none";

export function fixControl(issue: ContactIssueDTO): FixControl {
  switch (issue.field) {
    case "gst_no":
      // A duplicate GSTIN is not a typo, and offering a box to retype it would
      // suggest it is. The answer is to merge or delete one of the pair in Zoho,
      // which is not something this screen should do behind a text field.
      return issue.kind === "duplicate-gstin" || issue.kind === "both-party" ? "none" : "gstin";
    case "pan_no":
      return "pan";
    case "place_of_contact":
      return "state";
    case "gst_treatment":
      return "treatment";
    case "tds_tax_id":
      return "tds";
    case "udyam_reg_no":
      return "udyam";
    case "contact_name":
      return "name";
    default:
      return "none";
  }
}

/** The value a fix starts from, so the box opens holding what is on file. */
export function currentValue(contact: ContactDTO, control: FixControl): string {
  switch (control) {
    case "gstin":
      return contact.gst_no ?? "";
    case "pan":
      return contact.pan_no ?? "";
    case "state":
      return contact.place_of_contact ?? "";
    case "treatment":
      return contact.gst_treatment ?? "";
    case "tds":
      return contact.tds_tax_id ?? "";
    case "udyam":
      return contact.udyam_reg_no ?? "";
    case "name":
      return contact.contact_name ?? "";
    default:
      return "";
  }
}

/** Which field on the contact a control writes. */
export const FIX_FIELD: Record<Exclude<FixControl, "none">, string> = {
  gstin: "gst_no",
  pan: "pan_no",
  state: "place_of_contact",
  treatment: "gst_treatment",
  tds: "tds_tax_id",
  udyam: "udyam_reg_no",
  name: "contact_name",
};

// ── The bulk sheet ──────────────────────────────────────────────────────────

export const ACTION_ORDER: ContactImportAction[] = ["invalid", "new", "update", "unchanged"];

export const ACTION_LABEL: Record<ContactImportAction, string> = {
  invalid: "Needs fixing",
  new: "To be added",
  update: "To be changed",
  unchanged: "Unchanged",
};

/**
 * The same groups once the push has run — outcome tense, not intent tense.
 * "To be added: 8" over eight rows all badged "Added" reads as a queue that
 * never drained. The rows stay grouped by sheet action (see the rows
 * component's docblock for why); only the heading changes tense.
 */
export const PUSHED_ACTION_LABEL: Record<ContactImportAction, string> = {
  invalid: "Not sent — needs fixing",
  new: "Added",
  update: "Changed",
  unchanged: "Unchanged — not sent",
};

export const ACTION_STYLE: Record<ContactImportAction, string> = {
  invalid: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
  new: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  update: "bg-sky-500/10 text-sky-700 dark:text-sky-400",
  unchanged: "bg-muted text-muted-foreground",
};

/**
 * What happened to a row, in the past tense. Only shown once a push has run —
 * before that every row is `pending`, and labelling two hundred rows "Not sent"
 * would read as a problem rather than as "nothing has happened yet".
 */
export const ROW_STATUS_LABEL: Record<ContactImportRowStatus, string> = {
  pending: "Not sent",
  created: "Added",
  updated: "Changed",
  skipped: "Nothing to do",
  failed: "Zoho refused",
};

export const ROW_STATUS_STYLE: Record<ContactImportRowStatus, string> = {
  pending: "bg-muted text-muted-foreground",
  created: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  updated: "bg-sky-500/10 text-sky-700 dark:text-sky-400",
  skipped: "bg-muted text-muted-foreground",
  failed: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
};

/** The template's own headings, for a diff that points at a column. */
const FIELD_LABEL: Record<string, string> = {
  contact_name: "Display Name",
  company_name: "Trading Name",
  legal_name: "Legal Name",
  customer_sub_type: "Business or Individual",
  gst_treatment: "GST Treatment",
  gst_no: "GSTIN",
  pan_no: "PAN",
  country: "Country",
  place_of_contact: "State",
  billing_city: "City",
  billing_zip: "Postal Code",
  billing_address: "Billing Address",
  shipping_address: "Shipping Address",
  payment_terms: "Payment Terms",
  currency_code: "Currency",
  credit_limit: "Credit Limit",
  contact_name_1: "Primary Contact",
  contact_email_1: "Primary Email",
  contact_number_1: "Primary Phone",
  contact_name_2: "Secondary Contact",
  contact_email_2: "Secondary Email",
  contact_number_2: "Secondary Phone",
  msme_type: "MSME Type",
  udyam_reg_no: "Udyam Number",
  tds_tax_name: "TDS Section",
  tds_tax_id: "TDS Section",
  vendor_legal_entity: "Legal Entity",
  type_of_vendor: "Type of Vendor",
  business_category: "Business Category",
  beneficiary_name: "Beneficiary Name",
  bank_account_number: "Bank Account Number",
  bank_name: "Bank Name",
  bank_branch: "Branch",
  ifsc_swift_code: "IFSC / SWIFT",
};

export function fieldLabel(field: string): string {
  return FIELD_LABEL[field] ?? field;
}

/** An empty cell reads better as a dash than as nothing in a before/after. */
export function cellText(value: unknown): string {
  return value === null || value === undefined || value === "" ? "—" : String(value);
}

/**
 * A field's value in the words the screen uses, not the code Zoho stores.
 *
 * "TN → MH" is technically what the correction does and tells a CA nothing they
 * can check. Three of the fields a fix can touch are codes — the state, the GST
 * treatment, and the TDS rate, which is a nineteen-digit id — and every one of
 * them has a label the same screen already shows somewhere else.
 */
export function valueText(field: string, value: unknown, book: ContactBookDTO): string {
  const text = value === null || value === undefined ? "" : String(value);
  if (text === "") return "—";

  if (field === "place_of_contact") {
    return Object.entries(book.states).find(([, code]) => code === text)?.[0] ?? text;
  }

  if (field === "gst_treatment") {
    return Object.entries(book.treatments).find(([, code]) => code === text)?.[0] ?? text;
  }

  if (field === "tds_tax_id") {
    return book.tds_rates.find((rate) => rate.tax_id === text)?.tax_name ?? text;
  }

  return text;
}

export function groupByAction(
  rows: ContactImportRowDTO[]
): { action: ContactImportAction; rows: ContactImportRowDTO[] }[] {
  return ACTION_ORDER.map((action) => ({
    action,
    rows: rows.filter((row) => row.action === action),
  })).filter((group) => group.rows.length > 0);
}

/**
 * Warnings on rows that are otherwise fine.
 *
 * Counted apart from `invalid_rows` because they are a different question. An
 * invalid row will not be sent; a warned row will be, and the warning is the
 * last chance to say don't.
 */
export function warningCount(rows: ContactImportRowDTO[]): number {
  return rows.filter(
    (row) => row.action !== "invalid" && row.issues.some((issue) => issue.severity === "warning")
  ).length;
}

/**
 * Has this import been sent anywhere yet?
 *
 * The question the panel turns on: before, it shows what WOULD change and offers
 * a push; after, it shows what DID and offers no such thing. `status` rather
 * than a count, because a push that failed every row has still been run and must
 * not offer itself again as though it had not.
 */
export function hasBeenPushed(summary: ContactImportDTO): boolean {
  return summary.status === "committing" || summary.status === "done";
}
